import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as XLSX from 'xlsx'
import { createSchool } from '../../api/dashboard'
import AddSchoolModal from '../AddSchoolModal'
import { SegmentedControl, surface } from '../ui/surface'
import { IconAction, StatusDot } from './schoolEquipmentChrome'

const pageSize = 20

const fieldLabel =
	'flex flex-col gap-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500'
const fieldInput =
	'h-9 w-full rounded-xl bg-white/[0.04] px-3 text-sm font-normal normal-case tracking-normal text-muted-100 placeholder:text-muted-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400'
const sortHeader =
	'flex h-9 w-full items-center gap-2 rounded-xl px-3 text-left transition hover:bg-white/[0.04] hover:text-white'
const filterCol = 'flex h-full min-w-0 flex-col justify-between gap-3'
const activeBadge =
	'inline-flex items-center gap-1 rounded-lg bg-white/[0.06] px-2 py-1 text-xs font-medium text-muted-200'

const stabilityTone = {
	unstable: 'text-warning',
	offline: 'text-critical',
}

const focusRing =
	'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400'

const filterButton = active =>
	`inline-flex h-9 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl px-3 text-xs font-medium transition ${focusRing} ${
		active
			? 'bg-white/[0.1] text-white'
			: 'bg-white/[0.04] text-muted-300 hover:bg-white/[0.08] hover:text-white'
	}`

const sortIcon = (active, direction) =>
	active ? (direction === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'

const stabilityLabel = (stability, t) => {
	if (!Number.isFinite(stability?.percent)) return stability?.message || ''
	if (stability.percent >= 100) return t('devices.availabilityStable')
	return t('devices.availabilityPercent', { value: stability.percent })
}

const filterDevices = (devices, { school, region, status, trafficFilter }) => {
	let filtered = devices.filter(device => {
		const matchesSchool = device.school
			.toLowerCase()
			.includes(school.toLowerCase())
		const matchesRegion = device.region
			.toLowerCase()
			.includes(region.toLowerCase())
		const matchesStatus = status
			? device.status.toLowerCase() === status.toLowerCase()
			: true
		return matchesSchool && matchesRegion && matchesStatus
	})

	// Применяем сортировку по трафику (если выбрана)
	if (trafficFilter === 'topDownload') {
		// Сортировка всех устройств по скачанному трафику (по убыванию)
		filtered = [...filtered].sort((a, b) => {
			const aValue = Number(a.totalDownload) || 0
			const bValue = Number(b.totalDownload) || 0
			return bValue - aValue
		})
	} else if (trafficFilter === 'topUpload') {
		// Сортировка всех устройств по переданному трафику (по убыванию)
		filtered = [...filtered].sort((a, b) => {
			const aValue = Number(a.totalUpload) || 0
			const bValue = Number(b.totalUpload) || 0
			return bValue - aValue
		})
	}

	return filtered
}

const paginateDevices = (devices, currentPage) => {
	const start = (currentPage - 1) * pageSize
	return devices.slice(start, start + pageSize)
}

const formatThroughput = value => {
	if (value === null || value === undefined) return '—'
	const numeric = Number(value)
	if (Number.isNaN(numeric)) return value
	if (numeric >= 1000) {
		return `${(numeric / 1000).toFixed(1)} Гбит/с`
	}
	return `${numeric.toFixed(1)} Мбит/с`
}

const formatTraffic = value => {
	if (value == null || value === undefined) return '—'
	const numeric = Number(value)
	if (Number.isNaN(numeric)) return '—'
	if (numeric >= 1024) {
		return `${(numeric / 1024).toFixed(1)} ГБ`
	}
	return `${Math.round(numeric)} МБ`
}

const getSchoolIconText = schoolName => {
	if (!schoolName) return '??'

	// Извлекаем все цифры из названия школы
	const numbers = schoolName.match(/\d+/g)

	if (numbers && numbers.length > 0) {
		// Берем первую найденную цифру (или комбинацию цифр)
		// Если это одна цифра или короткая комбинация, показываем полностью
		// Если длинная комбинация, берем первые несколько цифр
		const numberStr = numbers[0]
		if (numberStr.length <= 3) {
			return numberStr
		}
		// Если цифр больше 3, берем первые 3
		return numberStr.slice(0, 3)
	}

	// Если цифр нет, используем первые 2 буквы как fallback
	return schoolName.slice(0, 2).toUpperCase()
}

const isRowAction = event => Boolean(event.target.closest('[data-row-action]'))

const openInNewTab = ipAddress => {
	if (!ipAddress) return
	window.open(
		`/schools/${encodeURIComponent(ipAddress)}`,
		'_blank',
		'noopener,noreferrer',
	)
}

const DeviceStatus = ({ status, visible = true, t }) => {
	if (visible === false) {
		return <StatusDot tone='muted' label={t('common.hidden')} />
	}
	return status?.toLowerCase() === 'up' ? (
		<StatusDot tone='ok' label={t('common.online')} />
	) : (
		<StatusDot tone='critical' label={t('common.offline')} />
	)
}

const SchoolBadge = ({ school, labelColor }) => (
	<span className='relative flex h-9 w-9 shrink-0 overflow-hidden rounded-xl'>
		{labelColor && (
			<span
				className='absolute inset-x-0 bottom-0 h-8'
				style={{ backgroundColor: labelColor }}
				aria-hidden
			/>
		)}
		<span
			className={`absolute inset-x-0 top-0 rounded-xl ${surface.card} ${
				labelColor ? 'bottom-px' : 'bottom-0'
			}`}
			aria-hidden
		/>
		<span
			className={`absolute inset-x-0 top-0 flex items-center justify-center rounded-xl bg-brand-400/15 font-display text-xs tabular-nums text-brand-100 ${
				labelColor ? 'bottom-px' : 'bottom-0'
			}`}
		>
			{getSchoolIconText(school)}
		</span>
	</span>
)

const TrafficMetric = ({ arrow, speed, total, speedClass, speedTitle, totalTitle }) => (
	<div className='flex min-w-0 flex-1 flex-col items-start gap-0.5 tabular-nums lg:w-28 lg:flex-none'>
		<span className={`text-sm font-medium ${speedClass}`} title={speedTitle}>
			<span className='mr-1 text-[10px] text-muted-500'>{arrow}</span>
			{speed}
		</span>
		<span className='font-mono text-[11px] text-muted-500' title={totalTitle}>
			Σ {total}
		</span>
	</div>
)

const DevicesTable = ({
	devices,
	onLogsClick,
	onSpeedtestClick,
	onDetailsClick = () => {},
	totalSchools = 0,
	className = '',
	externalRegionFilter = '', // Внешний фильтр по региону
	filterRequest = null, // { filters: { status?, trafficFilter? }, id } — применить и прокрутить к таблице
	userRole = '', // Роль пользователя для проверки доступа
	onSchoolCreated = () => {}, // Callback после создания школы
}) => {
	const { t } = useTranslation()
	const [filters, setFilters] = useState({
		school: '',
		region: '',
		status: '',
		trafficFilter: '',
	})
	const [currentPage, setCurrentPage] = useState(1)
	const [sortField, setSortField] = useState('school')
	const [sortDirection, setSortDirection] = useState('asc')
	const [isAddSchoolModalOpen, setIsAddSchoolModalOpen] = useState(false)
	const [isCreatingSchool, setIsCreatingSchool] = useState(false)
	const [createError, setCreateError] = useState('')
	const sectionRef = useRef(null)

	// Синхронизируем внешний фильтр по региону с внутренним фильтром
	useEffect(() => {
		if (externalRegionFilter) {
			setFilters(prev => ({ ...prev, region: externalRegionFilter }))
		} else {
			setFilters(prev => ({ ...prev, region: '' }))
		}
	}, [externalRegionFilter])

	// Новый id означает новый запрос, даже если фильтры те же: повторный клик снова применяет и прокручивает
	useEffect(() => {
		if (!filterRequest) return
		setFilters(prev => ({ ...prev, ...filterRequest.filters }))
		sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
	}, [filterRequest])

	useEffect(() => {
		setCurrentPage(1)
	}, [filters])

	useEffect(() => {
		setCurrentPage(1)
	}, [sortField, sortDirection])

	// Функция сортировки устройств
	const sortDevices = (devices, field, direction) => {
		if (!Array.isArray(devices) || devices.length === 0) return devices

		// Создаем копию массива для избежания мутации
		const devicesCopy = [...devices].filter(device => device != null)

		return devicesCopy.sort((a, b) => {
			let aValue, bValue

			switch (field) {
				case 'school':
					aValue = a.school || ''
					bValue = b.school || ''
					break
				case 'region':
					aValue = a.region || ''
					bValue = b.region || ''
					break
				case 'status':
					aValue = String(a.status || '')
						.toLowerCase()
						.trim()
					bValue = String(b.status || '')
						.toLowerCase()
						.trim()
					// Порядок приоритета для status
					const statusOrder = {
						up: 0,
						degraded: 1,
						down: 2,
						online: 0,
						offline: 2,
						'': 999, // для пустых значений
					}
					aValue = statusOrder[aValue] ?? 999
					bValue = statusOrder[bValue] ?? 999
					break
				case 'download':
					aValue = Number(a.download) || 0
					bValue = Number(b.download) || 0
					break
				case 'upload':
					aValue = Number(a.upload) || 0
					bValue = Number(b.upload) || 0
					break
				case 'totalDownload':
					aValue = Number(a.totalDownload) || 0
					bValue = Number(b.totalDownload) || 0
					break
				case 'totalUpload':
					aValue = Number(a.totalUpload) || 0
					bValue = Number(b.totalUpload) || 0
					break
				case 'ip':
					aValue = a.ip_address || a.ip || ''
					bValue = b.ip_address || b.ip || ''
					break
				default:
					return 0
			}

			// Применяем toLowerCase только для строковых значений
			if (typeof aValue === 'string' && typeof bValue === 'string') {
				aValue = aValue.toLowerCase()
				bValue = bValue.toLowerCase()
			}

			// Сравниваем значения
			if (aValue < bValue) return direction === 'asc' ? -1 : 1
			if (aValue > bValue) return direction === 'asc' ? 1 : -1

			// Если значения равны, используем вторичный критерий сортировки
			// Сначала по названию школы, затем по IP адресу
			const aSchool = (a.school || '').toLowerCase()
			const bSchool = (b.school || '').toLowerCase()
			if (aSchool < bSchool) return -1
			if (aSchool > bSchool) return 1

			// Если и школы одинаковые, сортируем по IP
			const aIp = (a.ip_address || a.ip || '').toLowerCase()
			const bIp = (b.ip_address || b.ip || '').toLowerCase()
			if (aIp < bIp) return -1
			if (aIp > bIp) return 1

			return 0
		})
	}

	// Обработчик клика по кнопке сортировки
	const handleSort = field => {
		if (sortField === field) {
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
		} else {
			setSortField(field)
			setSortDirection('asc')
		}
		// Сбрасываем страницу при изменении сортировки
		setCurrentPage(1)
	}

	const filtered = useMemo(
		() => filterDevices(devices, filters),
		[devices, filters],
	)
	// Если выбран фильтр по трафику, не применяем обычную сортировку (сортировка уже применена в filterDevices)
	const sorted = useMemo(() => {
		if (filters.trafficFilter) {
			return filtered // Уже отсортировано в filterDevices
		}
		return sortDevices(filtered, sortField, sortDirection)
	}, [filtered, sortField, sortDirection, filters.trafficFilter])

	const totalPages = Math.ceil(sorted.length / pageSize) || 1
	const paginated = useMemo(
		() => paginateDevices(sorted, currentPage),
		[sorted, currentPage],
	)

	// Проверка роли для создания школы (только admin или engineer)
	const canCreateSchool = userRole === 'admin' || userRole === 'engineer'

	const handleCreateSchool = async schoolData => {
		setIsCreatingSchool(true)
		setCreateError('')

		try {
			await createSchool(schoolData)
			setCreateError('')
			setIsAddSchoolModalOpen(false)
			// Вызываем callback для обновления данных
			if (onSchoolCreated) {
				onSchoolCreated()
			}
		} catch (error) {
			setCreateError(
				error.response?.data?.message || t('errors.failedToCreateSchool'),
			)
		} finally {
			setIsCreatingSchool(false)
		}
	}

	const openDevice = (event, device) => {
		if (isRowAction(event)) return
		if (event.ctrlKey || event.metaKey) {
			openInNewTab(device.ip_address || device.ip)
			return
		}
		onDetailsClick(device)
	}

	const handleExportToExcel = () => {
		// Подготавливаем данные для экспорта
		const exportData = sorted.map((device, index) => {
			let statusText = t('common.offline')
			if (device.visible === false) {
				statusText = t('common.hidden')
			} else if (device.status === 'up' || device.status === 'Up') {
				statusText = t('common.online')
			}

			return {
				'№': index + 1,
				Объект: device.school || '',
				'IP адрес': device.ip_address || device.ip || '',
				Регион: device.region || '',
				Лейбл: device.label_name || '—',
				Статус: statusText,
				'Download (Мбит/с)': device.download
					? parseFloat(device.download).toFixed(2)
					: '—',
				'Upload (Мбит/с)': device.upload
					? parseFloat(device.upload).toFixed(2)
					: '—',
				'Общий Download (МБ)': device.totalDownload
					? parseFloat(device.totalDownload).toFixed(2)
					: '—',
				'Общий Upload (МБ)': device.totalUpload
					? parseFloat(device.totalUpload).toFixed(2)
					: '—',
				'Стабильность подключения': device.connectionStability?.message || '',
			}
		})

		// Создаем рабочую книгу Excel
		const ws = XLSX.utils.json_to_sheet(exportData)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, t('devices.exportSheetName'))

		// Настраиваем ширину колонок
		const colWidths = [
			{ wch: 5 }, // №
			{ wch: 30 }, // Объект
			{ wch: 15 }, // IP адрес
			{ wch: 20 }, // Регион
			{ wch: 18 }, // Лейбл
			{ wch: 12 }, // Статус
			{ wch: 15 }, // Download
			{ wch: 15 }, // Upload
			{ wch: 18 }, // Общий Download
			{ wch: 18 }, // Общий Upload
			{ wch: 30 }, // Стабильность
		]
		ws['!cols'] = colWidths

		// Генерируем имя файла с текущей датой
		const date = new Date()
			.toLocaleDateString('ru-RU', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
			})
			.replace(/\//g, '-')
		const fileName = t('devices.exportFileName', { date })

		// Сохраняем файл
		XLSX.writeFile(wb, fileName)
	}

	return (
		<section
			id='devices-table'
			ref={sectionRef}
			className={`scroll-mt-24 rounded-3xl ${surface.section} p-4 sm:p-6 ${className}`}
		>
			<header className='mb-4 sm:mb-6 flex flex-col gap-3 sm:gap-4 sm:flex-row sm:items-center sm:justify-between'>
				<div>
					<div className='flex items-center gap-3'>
						<h3 className='font-display text-xl text-white md:text-2xl'>
							{t('devices.list')}
						</h3>
						{(sortField !== 'school' ||
							sortDirection !== 'asc' ||
							filters.trafficFilter) && (
							<div className='flex items-center gap-2'>
								{(sortField !== 'school' || sortDirection !== 'asc') && (
									<div className={activeBadge}>
										<span className='material-icons text-sm'>sort</span>
										{sortField === 'school' && t('devices.object')}
										{sortField === 'region' && t('devices.region')}
										{sortField === 'status' && t('devices.status')}
										{sortField === 'download' && t('devices.download')}
										{sortField === 'upload' && t('devices.upload')}
										{sortField === 'totalDownload' &&
											t('devices.totalDownload')}
										{sortField === 'totalUpload' && t('devices.totalUpload')}
										{sortField === 'ip' && t('devices.ip')}
										<span className='material-icons text-sm'>
											{sortDirection === 'asc'
												? 'arrow_upward'
												: 'arrow_downward'}
										</span>
									</div>
								)}
								{filters.trafficFilter === 'topDownload' && (
									<div className={activeBadge}>
										<span className='material-icons text-sm'>filter_list</span>
										{t('devices.topDownloaded')}
									</div>
								)}
								{filters.trafficFilter === 'topUpload' && (
									<div className={activeBadge}>
										<span className='material-icons text-sm'>filter_list</span>
										{t('devices.topUploaded')}
									</div>
								)}
							</div>
						)}
					</div>
					<p className='mt-1 text-sm text-muted-400'>
						{t('devices.totalConnected', {
							count: totalSchools || devices.length,
						})}
					</p>
				</div>
				<div className='flex flex-wrap items-center gap-3'>
					{canCreateSchool && (
						<button
							type='button'
							onClick={() => setIsAddSchoolModalOpen(true)}
							className='inline-flex items-center gap-1.5 rounded-xl bg-positive/15 px-3 py-2 text-xs font-semibold text-positive transition hover:bg-positive/25'
						>
							<span className='material-icons text-base'>add</span>
							{t('devices.addSchool')}
						</button>
					)}
					<button
						type='button'
						onClick={handleExportToExcel}
						className='inline-flex items-center gap-1.5 rounded-xl bg-white/[0.04] px-3 py-2 text-xs font-medium text-muted-300 transition hover:bg-white/[0.08] hover:text-white'
					>
						<span className='material-icons text-base'>download</span>
						{t('common.export')}
					</button>
					<button
						type='button'
						onClick={() => {
							setFilters({
								school: '',
								region: '',
								status: '',
								trafficFilter: '',
							})
							setSortField('school')
							setSortDirection('asc')
						}}
						className='inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium text-muted-400 transition hover:bg-white/[0.06] hover:text-white'
					>
						<span className='material-icons text-base'>refresh</span>
						{t('common.reset')}
					</button>
				</div>
			</header>

			<div className='flex flex-col gap-4'>
					<div className='grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
						<label className={`${fieldLabel} ${filterCol}`}>
							<button
								onClick={() => handleSort('school')}
								className={`${sortHeader} ${sortField === 'school' ? 'bg-white/[0.04] text-white' : ''}`}
							>
								<span className='material-icons text-sm text-muted-400'>
									school
								</span>
								{t('devices.object')}
								<span
									className={`material-icons text-sm transition-colors ${
										sortField === 'school' ? 'text-brand-300' : 'text-muted-500'
									}`}
								>
									{sortField === 'school'
										? sortDirection === 'asc'
											? 'arrow_upward'
											: 'arrow_downward'
										: 'unfold_more'}
								</span>
							</button>
							<div className='relative'>
								<input
									type='text'
									placeholder={t('devices.schoolName')}
									value={filters.school}
									onChange={event =>
										setFilters(prev => ({
											...prev,
											school: event.target.value,
										}))
									}
									className={fieldInput}
								/>
							</div>
						</label>
						<label className={`${fieldLabel} ${filterCol}`}>
							<button
								onClick={() => handleSort('region')}
								className={`${sortHeader} ${sortField === 'region' ? 'bg-white/[0.04] text-white' : ''}`}
							>
								<span className='material-icons text-sm text-muted-400'>
									location_city
								</span>
								{t('devices.region')}
								<span
									className={`material-icons text-sm transition-colors ${
										sortField === 'region' ? 'text-brand-300' : 'text-muted-500'
									}`}
								>
									{sortField === 'region'
										? sortDirection === 'asc'
											? 'arrow_upward'
											: 'arrow_downward'
										: 'unfold_more'}
								</span>
							</button>
							<div className='relative'>
								<input
									type='text'
									placeholder={t('devices.region')}
									value={filters.region}
									onChange={event =>
										setFilters(prev => ({
											...prev,
											region: event.target.value,
										}))
									}
									className={fieldInput}
								/>
							</div>
						</label>
						<label className={`${fieldLabel} ${filterCol}`}>
							<button
								onClick={() => handleSort('status')}
								className={`${sortHeader} ${sortField === 'status' ? 'bg-white/[0.04] text-white' : ''}`}
								title={t('devices.statusTooltip')}
							>
								<span className='material-icons text-sm text-muted-400'>
									check_circle
								</span>
								{t('devices.status')}
								<span
									className={`material-icons text-sm transition-colors ${
										sortField === 'status' ? 'text-brand-300' : 'text-muted-500'
									}`}
								>
									{sortField === 'status'
										? sortDirection === 'asc'
											? 'arrow_upward'
											: 'arrow_downward'
										: 'unfold_more'}
								</span>
							</button>
							<div className='flex h-9 items-center gap-2 normal-case tracking-normal'>
								{[
									{ id: 'up', dot: 'bg-positive', label: t('common.online') },
									{ id: 'down', dot: 'bg-critical', label: t('common.offline') },
								].map(option => {
									const active = filters.status === option.id
									return (
										<button
											key={option.id}
											type='button'
											aria-pressed={active}
											onClick={() =>
												setFilters(prev => ({
													...prev,
													status: prev.status === option.id ? '' : option.id,
												}))
											}
											className={filterButton(active)}
										>
											<span
												className={`h-1.5 w-1.5 rounded-full ${option.dot}`}
												aria-hidden
											/>
											{option.label}
										</button>
									)
								})}
							</div>
						</label>
						<div className={filterCol}>
							<div className='flex h-9 items-center gap-2'>
								{[
									{ field: 'download', icon: 'download', iconClass: 'text-accent-cyan', label: t('devices.download'), title: t('devices.downloadSpeedTooltip') },
									{ field: 'upload', icon: 'upload', iconClass: 'text-accent-pink', label: t('devices.upload'), title: t('devices.uploadSpeedTooltip') },
								].map(option => {
									const active = sortField === option.field
									return (
										<button
											key={option.field}
											type='button'
											aria-pressed={active}
											onClick={() => handleSort(option.field)}
											className={filterButton(active)}
											title={option.title}
										>
											<span className={`material-icons text-sm ${option.iconClass}`}>
												{option.icon}
											</span>
											<span className='truncate'>{option.label}</span>
											<span className={`material-icons text-sm ${active ? 'text-white' : 'text-muted-500'}`}>
												{sortIcon(active, sortDirection)}
											</span>
										</button>
									)
								})}
							</div>
							<div className='flex h-9 items-center gap-2'>
								{[
									{ id: 'topDownload', iconClass: 'text-accent-cyan', label: t('devices.totalDownloaded'), title: t('devices.totalDownloadTooltip') },
									{ id: 'topUpload', iconClass: 'text-accent-pink', label: t('devices.totalUploaded'), title: t('devices.totalUploadTooltip') },
								].map(option => {
									const active = filters.trafficFilter === option.id
									return (
										<button
											key={option.id}
											type='button'
											aria-pressed={active}
											onClick={() =>
												setFilters(prev => ({
													...prev,
													trafficFilter: prev.trafficFilter === option.id ? '' : option.id,
												}))
											}
											className={filterButton(active)}
											title={option.title}
										>
											<span className={`text-xs font-semibold ${option.iconClass}`}>Σ</span>
											<span className='truncate'>{option.label}</span>
											<span className={`material-icons text-sm ${active ? 'text-white' : 'text-muted-500'}`}>
												{active ? 'arrow_downward' : 'unfold_more'}
											</span>
										</button>
									)
								})}
							</div>
						</div>
					</div>

					<div className='flex flex-col gap-2'>
						{paginated.map((device, index) => {
							const ipAddress = device.ip_address || device.ip
							const labelColor = device.label_color || '#9385ff'
							const stability = device.connectionStability
							const availability = stabilityLabel(stability, t)
							return (
								<article
									key={`${ipAddress || 'unknown'}-${device.school || 'unnamed'}-${index}`}
									tabIndex={0}
									title={t('devices.detailsTooltip')}
									onClick={event => openDevice(event, device)}
									onAuxClick={event => {
										if (event.button !== 1 || isRowAction(event)) return
										event.preventDefault()
										openInNewTab(ipAddress)
									}}
									onKeyDown={event => {
										if (isRowAction(event)) return
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault()
											onDetailsClick(device)
										}
									}}
									className={`group cursor-pointer rounded-2xl px-4 py-3 transition hover:bg-white/[0.06] ${focusRing} ${
										index % 2 === 0 ? surface.card : 'bg-surface-2/50'
									}`}
								>
									<div className='grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-3 lg:flex lg:gap-3'>
										<div className='flex min-w-0 items-center gap-3 lg:mr-3 lg:flex-1'>
											<SchoolBadge
												school={device.school}
												labelColor={device.label_color}
											/>
											<div className='min-w-0 flex-1'>
												<p
													className='truncate text-sm font-medium text-white'
													title={device.school}
												>
													{device.school}
												</p>
												<p className='mt-0.5 truncate text-[12px] text-muted-500'>
													<span className='font-mono tabular-nums'>{ipAddress}</span>
													{device.region && <span> · {device.region}</span>}
													{device.label_name && (
														<span>
															{' · '}
															<span style={{ color: labelColor }}>
																{device.label_name}
															</span>
														</span>
													)}
												</p>
											</div>
										</div>

										<div className='flex flex-col items-end gap-0.5 self-start text-right lg:mr-3 lg:w-28 lg:self-center'>
												<DeviceStatus
													status={device.status}
													visible={device.visible}
													t={t}
												/>
												{availability && (
													<span
														className={`whitespace-nowrap text-[11px] tabular-nums leading-tight ${stabilityTone[stability.type] || 'text-muted-500'}`}
														title={t('devices.stabilityTooltip')}
													>
														{availability}
													</span>
												)}
											</div>

											<div className='flex min-w-0 items-center gap-4 lg:gap-2'>
												<TrafficMetric
													arrow='↓'
													speed={formatThroughput(device.download)}
													total={formatTraffic(device.totalDownload)}
													speedClass='text-accent-cyan'
													speedTitle={t('devices.downloadSpeedTooltip')}
													totalTitle={t('devices.totalDownloadTooltip')}
												/>
												<TrafficMetric
													arrow='↑'
													speed={formatThroughput(device.upload)}
													total={formatTraffic(device.totalUpload)}
													speedClass='text-accent-pink'
													speedTitle={t('devices.uploadSpeedTooltip')}
													totalTitle={t('devices.totalUploadTooltip')}
												/>
											</div>

											<div data-row-action className='flex shrink-0 items-center gap-1.5'>
												<IconAction
													icon='timeline'
													tone='brand'
													title={t('devices.historyTooltip')}
													onClick={() => onLogsClick(ipAddress)}
												/>
												<IconAction
													icon='speed'
													tone='cyan'
													title={t('devices.speedtestTooltip')}
													onClick={() => onSpeedtestClick(device)}
												/>
												<span
													className='material-icons hidden h-8 w-8 items-center justify-center text-[18px] text-muted-600 transition group-hover:text-muted-300 lg:inline-flex'
													aria-hidden
												>
													chevron_right
												</span>
											</div>
									</div>
								</article>
							)
						})}
						{paginated.length === 0 && (
							<p className={`rounded-2xl py-10 text-center text-sm text-muted-500 ${surface.card}`}>
								{t('common.noDataToDisplay')}
							</p>
						)}
					</div>
			</div>

			<div className='mt-4 flex flex-col items-center justify-between gap-3 text-xs text-muted-400 sm:mt-6 sm:flex-row sm:gap-4 sm:text-sm'>
				<p>
					{t('devices.shown', {
						shown: paginated.length,
						total: sorted.length,
					})}
				</p>
				{totalPages > 1 && (
					<SegmentedControl
						options={Array.from({ length: totalPages }, (_, index) => ({
							id: index + 1,
							label: index + 1,
						}))}
						value={currentPage}
						onChange={setCurrentPage}
					/>
				)}
			</div>

			{/* Модальное окно для добавления школы */}
			<AddSchoolModal
				isOpen={isAddSchoolModalOpen}
				onClose={() => {
					setIsAddSchoolModalOpen(false)
					setCreateError('')
				}}
				onSubmit={handleCreateSchool}
				isLoading={isCreatingSchool}
				error={createError}
			/>
		</section>
	)
}

export default DevicesTable
